<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { AppButton, AppLoading, AppPagination, FormInput, useDialog, useToast } from 'duanaga-ui'
import FetchError from '@/FetchError'
import BatchApi from '@/api/Batch'
import { useDate } from '@/composables/date'
import useBatchStore from '@/stores/batch'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const { showDialog } = useDialog()
const { toastError } = useToast()
const batchStore = useBatchStore()
const { batches, links, loading, meta, notFound, refetch, selected } = storeToRefs(batchStore)
const error = ref<string | null>(null)
const search = ref('')
const { formatHumanDateTime, formatHumanDate } = useDate()
const adminDashboardUrl = import.meta.env.VITE_ADMIN_DASHBOARD_URL?.trim()
const authStore = useAuthStore()

const dashboardLink = computed(() => {
  if (!adminDashboardUrl || !authStore.user) return null
  
  const url = new URL(adminDashboardUrl)
  url.searchParams.set('token', authStore.token ?? '')
  url.searchParams.set('role', authStore.user.role)
  url.searchParams.set('email', authStore.user.email)
  url.searchParams.set('name', authStore.user.name)
  return url.toString()
})

const batchCriteria = computed(() => route.query as BatchCriteria)

watch(refetch, (shouldRefresh) => {
  if (shouldRefresh) {
    void fetchBatches(batchCriteria.value)
  }
})

onMounted(() => {
  void fetchBatches(batchCriteria.value)
  search.value = (route.query.search as string) || ''
})

onBeforeRouteUpdate((to) => {
  const criteria = to.query as BatchCriteria
  search.value = (to.query.search as string) || ''
  void fetchBatches(criteria)
})

const fetchBatches = async (criteria: BatchCriteria): Promise<void> => {
  loading.value = true
  error.value = null
  notFound.value = false

  try {
    const response = await BatchApi.paginate<Paginate<Batch>>(criteria)
    batches.value = response.data
    meta.value = response.meta
    links.value = response.links

    if (!batches.value.length) {
      notFound.value = true
    }
  } catch (err) {
    const fetchError = err as FetchError
    toastError(fetchError.message)
  } finally {
    loading.value = false
    refetch.value = false
  }
}

const searchBatches = async () => {
  router.push({
    query: {
      ...route.query,
      search: search.value || undefined
    }
  })
}

const openCreateModal = (): void => {
  showDialog(defineAsyncComponent(() => import('@/components/BatchAddModal.vue')))
}

const openEditModal = (batch: Batch): void => {
  selected.value = batch
  showDialog(defineAsyncComponent(() => import('@/components/BatchEditModal.vue')))
}

const confirmDelete = (batch: Batch): void => {
  selected.value = batch
  showDialog(defineAsyncComponent(() => import('@/components/BatchDeleteModal.vue')))
}
</script>

<template>
  <section class="flex flex-col gap-16 rounded-16 border border-grey-30 bg-white p-16 shadow-sm">
    <header class="flex flex-wrap items-center justify-between gap-16">
      <div>
        <h1 class="text-20 font-600 text-indigo-110">Batches</h1>
      </div>

      <div class="flex w-full flex-wrap gap-16">
        <FormInput
          v-model="search"
          type="search"
          placeholder="Search number, brand…"
          class="flex-1"
          @keyup.enter="searchBatches"
        />
        <a
          v-if="dashboardLink && (authStore.user?.role === 'admin' || authStore.user?.role === 'superadmin')"
          :href="dashboardLink"
          class="inline-flex h-40 items-center justify-center gap-8 rounded-full border border-indigo-20 bg-indigo-5 px-16 text-14 font-600 text-indigo-110 transition-colors hover:bg-indigo-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
          Open Dashboard
        </a>
        <AppButton
          color="black"
          text="Add batch"
          icon="add"
          @click="openCreateModal"
        />
      </div>
    </header>

    <div class="overflow-x-auto rounded-8 border border-grey-30 bg-white">
      <table class="middle min-w-full border-collapse">
        <thead>
          <tr>
            <th
              class="border-b border-grey-20 px-16 py-[0.85rem] text-left text-[0.85rem] font-500 uppercase tracking-[0.1em] text-grey-70"
            >
              Batch
            </th>
            <th
              class="border-b border-grey-20 px-16 py-[0.85rem] text-center text-[0.85rem] font-500 uppercase tracking-[0.1em] text-grey-70"
            >
              Codes Count
            </th>
            <th
              class="border-b border-grey-20 px-16 py-[0.85rem] text-left text-[0.85rem] font-500 uppercase tracking-[0.1em] text-grey-70"
            >
              Production Date
            </th>
            <th
              class="border-b border-grey-20 px-16 py-[0.85rem] text-left text-[0.85rem] font-500 uppercase tracking-[0.1em] text-grey-70"
            >
              Expiry Date
            </th>
            <th
              class="border-b border-grey-20 px-16 py-[0.85rem] text-left text-[0.85rem] font-500 uppercase tracking-[0.1em] text-grey-70"
            >
              Created
            </th>
            <th
              class="border-b border-grey-20 px-16 py-[0.85rem] text-right text-[0.85rem] font-500 uppercase tracking-[0.1em] text-grey-70"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td
              class="border-b border-grey-20 px-16 py-[0.85rem] text-indigo-110"
              colspan="6"
            >
              <AppLoading />
            </td>
          </tr>

          <tr v-else-if="error">
            <td
              class="border-b border-grey-20 px-16 py-[0.85rem] text-indigo-110"
              colspan="6"
            >
              {{ error }}
            </td>
          </tr>

          <tr v-else-if="!batches.length">
            <td
              class="border-b border-grey-20 px-16 py-[0.85rem] text-indigo-110"
              colspan="6"
            >
              No batches found.
            </td>
          </tr>

          <tr
            v-for="batch in batches"
            v-else
            :key="batch.id"
            class="hover:bg-grey-5"
          >
            <td class="border-b border-grey-20 px-16 py-16">
              <div class="flex flex-col gap-4">
                <div>
                  <router-link
                    :to="{ name: 'batch-details', params: { id: batch.id } }"
                    class="text-16 font-600 text-indigo-100 hover:text-indigo-110 hover:underline"
                  >
                    {{ batch.number }}
                  </router-link>
                </div>
                <div class="text-14 text-grey-70">
                  {{ batch.brand }} &middot; {{ batch.product }} &middot; {{ batch.variant }}
                </div>
              </div>
            </td>
            <td class="border-b border-grey-20 px-16 py-16 text-center">
              <span
                class="inline-flex items-center rounded-full bg-indigo-10 px-12 py-4 text-14 font-500 text-indigo-100"
              >
                {{ batch.code_count }}
              </span>
            </td>
            <td class="border-b border-grey-20 px-16 py-16 text-14 text-indigo-110">
              {{ formatHumanDate(batch.production_date) }}
            </td>
            <td class="border-b border-grey-20 px-16 py-16 text-14 text-indigo-110">
              {{ formatHumanDate(batch.expiry_date) }}
            </td>
            <td class="border-b border-grey-20 px-16 py-16 text-14 text-indigo-110">
              {{ formatHumanDateTime(batch.created_at) }}
            </td>
            <td class="border-b border-grey-20 px-16 py-16 text-right">
              <div class="flex justify-end gap-8">
                <AppButton
                  text="View"
                  size="sm"
                  @click="router.push({ name: 'batch-details', params: { id: batch.id } })"
                />
                <AppButton
                  text="Edit"
                  size="sm"
                  @click="openEditModal(batch)"
                />
                <AppButton
                  color="red"
                  text="Delete"
                  size="sm"
                  @click="confirmDelete(batch)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <section
      v-if="meta"
      class="flex justify-center"
    >
      <AppPagination :meta="meta" />
    </section>
  </section>
</template>
